import { useEffect, useState } from "react";
import { api, formatCHF } from "../lib/api";
import { useT } from "../i18n/I18nContext";
import { Link } from "react-router-dom";
import { Download, Eye, FileText } from "lucide-react";

export default function ControllerDashboard() {
  const t = useT();
  const [groups, setGroups] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [stats, setStats] = useState({ groups: 0, totalPaid: 0, openAuctions: 0, auditEntries: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [gs, pays, aucs, audit] = await Promise.all([
          api.get("/groups"),
          api.get("/payments"),
          api.get("/auctions"),
          api.get("/audit-log"),
        ]);
        setGroups(gs.data);
        setAuditLog(audit.data.slice(0, 10));
        const totalPaid = pays.data.filter((p) => ["paid", "checked", "released"].includes(p.status)).reduce((a, b) => a + (b.amount || 0), 0);
        const openAuctions = aucs.data.filter((a) => a.status === "open").length;
        setStats({ groups: gs.data.length, totalPaid, openAuctions, auditEntries: audit.data.length });
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="mb-8">
        <div className="nv-label">{t("controller.title")}</div>
        <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#0F4C3A] mt-2" data-testid="controlling-dashboard-title">
          {t("controller.overview")}
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#E2E8E4] rounded-md overflow-hidden mb-8">
        <div className="p-6 border-r border-b md:border-b-0 border-[#E2E8E4] bg-white">
          <div className="nv-label">{t("admin.stats.groups")}</div>
          <div className="font-mono-nv text-3xl font-semibold text-[#0F4C3A] mt-3">{stats.groups}</div>
        </div>
        <div className="p-6 border-r border-b md:border-b-0 border-[#E2E8E4] bg-white">
          <div className="nv-label">{t("controller.payment_sum")}</div>
          <div className="font-mono-nv text-3xl font-semibold text-[#0F4C3A] mt-3">{formatCHF(stats.totalPaid)}</div>
        </div>
        <div className="p-6 border-r border-b md:border-b-0 border-[#E2E8E4] bg-white">
          <div className="nv-label">{t("admin.stats.open_auctions")}</div>
          <div className="font-mono-nv text-3xl font-semibold text-[#0F4C3A] mt-3">{stats.openAuctions}</div>
        </div>
        <div className="p-6 bg-[#0F4C3A] text-white">
          <div className="nv-label !text-[#D4AF37]">{t("controller.audit_count")}</div>
          <div className="font-mono-nv text-3xl font-semibold mt-3">{stats.auditEntries}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nv-border bg-white rounded-md">
          <div className="p-5 border-b border-[#E2E8E4] flex items-center justify-between">
            <div>
              <div className="nv-label">{t("nav.groups")}</div>
              <div className="font-heading text-lg font-medium text-[#0F4C3A]">{t("controller.monitored_groups")}</div>
            </div>
          </div>
          <div className="divide-y divide-[#E2E8E4]">
            {groups.map((g) => (
              <Link
                key={g.id}
                to={`/admin/groups/${g.id}`}
                data-testid={`controller-group-${g.id}`}
                className="flex items-center justify-between px-5 py-4 hover:bg-[#F8FAF9]"
              >
                <div>
                  <div className="font-medium text-[#0F4C3A]">{g.name}</div>
                  <div className="text-xs text-[#475569] mt-1">{g.shares} · {g.start_date}</div>
                </div>
                <Eye className="w-4 h-4 text-[#94A3B8]" />
              </Link>
            ))}
          </div>
        </div>

        <div className="nv-border bg-white rounded-md">
          <div className="p-5 border-b border-[#E2E8E4] flex items-center justify-between">
            <div>
              <div className="nv-label">{t("nav.audit")}</div>
              <div className="font-heading text-lg font-medium text-[#0F4C3A]">{t("controller.recent_activity")}</div>
            </div>
            <Link to="/audit-log" className="text-xs font-semibold tracking-widest uppercase text-[#0F4C3A] hover:text-[#D4AF37]">
              {t("controller.see_all")}
            </Link>
          </div>
          <div className="divide-y divide-[#E2E8E4]">
            {auditLog.map((log) => (
              <div key={log.id} className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono-nv text-xs text-[#0F4C3A] bg-[#F8FAF9] px-2 py-0.5 rounded">{log.action}</span>
                  <span className="text-xs text-[#475569]">{log.entity_type}</span>
                </div>
                <div className="text-xs text-[#94A3B8] mt-1">
                  {log.user_email} · {new Date(log.created_at).toLocaleString("de-CH")}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 nv-border bg-white rounded-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="w-5 h-5 text-[#0F4C3A]" />
          <div className="font-heading text-lg font-medium text-[#0F4C3A]">{t("controller.export_title")}</div>
        </div>
        <div className="flex flex-wrap gap-3">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/export/payments.csv`}
            target="_blank"
            rel="noreferrer"
            data-testid="export-payments-csv"
            className="nv-btn-primary px-5 py-2.5 rounded-md font-medium inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> {t("controller.export_payments")}
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/export/audit.csv`}
            target="_blank"
            rel="noreferrer"
            data-testid="export-audit-csv"
            className="nv-btn-primary px-5 py-2.5 rounded-md font-medium inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> {t("controller.export_audit")}
          </a>
        </div>
      </div>
    </div>
  );
}
